import { type RefObject, useEffect } from "react";
import { isKeyboardInput, nextFocus } from "./modalFocus";

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Makes an overlay a modal in deed, not only in `aria-modal` (review 0027 G2): focus moves in,
 * Tab goes round inside, Escape asks to close, and when it closes the focus returns to where it
 * came from. Not a native `<dialog>`: that leaves the scaled table canvas for the top layer.
 */
export function useModalFocus(
  container: RefObject<HTMLElement | null>,
  open: boolean,
  options: { initial?: RefObject<HTMLElement | null>; onEscape?: () => void } = {},
): void {
  const { initial, onEscape } = options;
  useEffect(() => {
    const node = container.current;
    if (!open || node === null) return;
    const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const items = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
    (initial?.current ?? items()[0])?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onEscape !== undefined) {
        event.preventDefault();
        onEscape();
        return;
      }
      if (event.key !== "Tab") return;
      const list = items();
      const next = nextFocus(
        list.length,
        list.indexOf(document.activeElement as HTMLElement),
        event.shiftKey,
      );
      event.preventDefault();
      if (next !== null) list[next]?.focus();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      back?.focus({ preventScroll: true });
    };
  }, [container, open, initial, onEscape]);
}

let keyboard = false;
let listening = false;

/**
 * Was the player's last input a keyboard key? Focus is moved for them only then: a focused card
 * lifts, and moving focus for mouse and touch players would change what they see (G1).
 */
export function lastInputWasKeyboard(): boolean {
  return keyboard;
}

/** Starts watching; safe to call more than once. */
export function watchInput(): void {
  if (listening) return;
  listening = true;
  window.addEventListener(
    "keydown",
    (event) => {
      if (isKeyboardInput(event.key)) keyboard = true;
    },
    true,
  );
  window.addEventListener(
    "pointerdown",
    () => {
      keyboard = false;
    },
    true,
  );
}
